import { requestLaravel } from "../../../../../../utils/api";
import { IProjectStatusAndReadiness, IImplementationReadiness, IStakeholderCommunicationReadiness, IRiskAndQualityGate, IEconomicAndCommercialViability, IFeasibilityOption, IProjectOptionRow, IProjectFeasibilityAndOptionsAnalysis, ICbaCeaIntelligenceEngine, IRiskAndSensitivityAnalysisEngine } from "./_models";
import { ApiResponse } from "../../../../core/_models";
import { getProjectFieldSection, saveProjectFieldSection, withProjectFieldValues } from "../../../core/project-fields/_request";
import type { StepTab } from "../../ProjectInitiation-steps/core/_models";

const PREFEASIBILITY_STAGE_ID = 3;
// Stage key used by stage-neutral table APIs (project options) and section documents.
export const STAGE_THREE_KEY = "project_stage_3";

const ECONOMIC_AND_COMMERCIAL_VIABILITY_SECTION = "economic_and_commercial_viability";
export const CBA_CEA_INTELLIGENCE_SECTION = "cba_cea_intelligence";
// Sections the step's supporting documents are stored against.
export const PROJECT_STATUS_AND_READINESS_SECTION = "project_status_and_readiness";
export const IMPLEMENTATION_READINESS_SECTION = "implementation_readiness";
export const STAKEHOLDER_AND_COMMUNICATION_READINESS_SECTION = "stakeholder_and_communication_readiness";
export const RISK_AND_QUALITY_GATE_SECTION = "risk_and_quality_gate";
// Sub-section (tab) of risk_and_sensitivity_analysis; the Risk Analysis tab saves on the risk-management API.
const SENSITIVITY_ANALYSIS_SECTION = "rsa_sensitivity_analysis";

function readStageSection<T>(projectId: string | number, sectionKey: string, signal?: AbortSignal) {
    return getProjectFieldSection(projectId, PREFEASIBILITY_STAGE_ID, sectionKey, signal)
        .then((response) => withProjectFieldValues<T>(response));
}

function writeStageSection<T>(projectId: string | number, sectionKey: string, payload: object, signal?: AbortSignal) {
    return saveProjectFieldSection(projectId, PREFEASIBILITY_STAGE_ID, sectionKey, payload, signal)
        .then((response) => withProjectFieldValues<T>(response));
}

// Saves one tab of a tab-wise step through its project-fields sub-section, sending only the
// tab's fields; the response holds only that tab's fields.
export function saveStepTab(project_id: number, tab: StepTab & { section: string }, values: object) {
    const payload: Record<string, unknown> = {};
    tab.fields.forEach((field) => {
        payload[field] = (values as Record<string, unknown>)[field];
    });
    return writeStageSection<Record<string, unknown>>(project_id, tab.section, payload);
}

// A section's saved values as form values: fields not saved yet come back as null and keep their defaults.
export function updateStakeholderCommunicationReadiness(updateData: IStakeholderCommunicationReadiness & { project_id: number }) {
    return requestLaravel<ApiResponse<IStakeholderCommunicationReadiness>>(
        "/project-stage-three/stakeholder-communication-readiness/update",
        updateData
    );
}

export function getStakeholderCommunicationReadiness(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IStakeholderCommunicationReadiness>>(
        "/project-stage-three/stakeholder-communication-readiness/get",
        { project_id },
        { signal }
    );
}

export function getProjectStatusAndReadiness(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectStatusAndReadiness>>(
        "/project-stage-three/project-status-readiness/get",
        { project_id },
        { signal }
    );
}

interface UpdateProjectStatusAndReadinessPayload extends IProjectStatusAndReadiness {
    project_id: number;
}

export function updateProjectStatusAndReadiness(updateData: UpdateProjectStatusAndReadinessPayload) {
    return requestLaravel<ApiResponse<IProjectStatusAndReadiness>>(
        "/project-stage-three/project-status-readiness/update",
        updateData
    );
}

export function updateImplementationReadiness(updateData: IImplementationReadiness & { project_id: number }) {
    return requestLaravel<ApiResponse<IImplementationReadiness>>(
        "/project-stage-three/implementation-readiness/update",
        updateData
    );
}

export function getImplementationReadiness(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IImplementationReadiness>>(
        "/project-stage-three/implementation-readiness/get",
        { project_id },
        { signal }
    );
}

export function saveProjectStageThree(payload: { project_id: number; dk_stage: string }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<void>>(
        "/project-stage-three/stage/submit",
        { project_id: payload.project_id },
        { signal }
    );
}

export function getFeasibilityReadinessPercentage(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ readiness_percentage: number; average_risk_score: number }>>(
        "/project-stage-three/feasibility-readiness/get",
        payload,
        { signal }
    );
}


/* =========================
   ECONOMIC & COMMERCIAL VIABILITY
========================= */

// One request for all seven sub-sections (tabs); a save sends only the fields it has.
export function getEconomicAndCommercialViability(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<Partial<IEconomicAndCommercialViability>>(project_id, ECONOMIC_AND_COMMERCIAL_VIABILITY_SECTION, signal);
}

export function updateEconomicAndCommercialViability(updateData: IEconomicAndCommercialViability & { project_id: number }) {
    return writeStageSection<IEconomicAndCommercialViability>(updateData.project_id, ECONOMIC_AND_COMMERCIAL_VIABILITY_SECTION, updateData);
}


/* =========================
   OPTIONS IDENTIFICATION & SCREENING
========================= */

export function getProjectOptions(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectOptionRow[]>>(
        "/project/options/list",
        { project_id, stage_id: STAGE_THREE_KEY },
        { signal }
    );
}

export function saveProjectOption(payload: IFeasibilityOption & { project_id: number }, signal?: AbortSignal) {
    const { row_id, project_id, option_name, description, capital_cost, operating_cost, delivery_time, expected_benefits, strategic_alignment, complexity, risk_rating } = payload;
    return requestLaravel<ApiResponse<IProjectOptionRow>>(
        "/project/options/save",
        {
            project_id,
            stage_id: STAGE_THREE_KEY,
            ...(row_id ? { row_id } : {}),
            option_name,
            description,
            capital_cost,
            operating_cost,
            delivery_time,
            expected_benefits,
            strategic_alignment,
            complexity,
            risk_rating,
        },
        { signal }
    );
}

export function deleteProjectOption(payload: { project_id: number; row_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ row_id: number }>>(
        "/project/options/delete",
        { ...payload, stage_id: STAGE_THREE_KEY },
        { signal }
    );
}

// Builds the justification map ({ "<row_id>": "<html>" }) from the listed option rows.
export function toJustificationMap(rows: IProjectOptionRow[]): Record<string, string> {
    return rows.reduce<Record<string, string>>((acc, row) => {
        if (row.row_id && row.option_analysis_description) {
            acc[String(row.row_id)] = row.option_analysis_description;
        }
        return acc;
    }, {});
}

// Saves the option justifications; the API responds with the full option list, which is
// mapped back to the step's form shape.
export async function saveOptionJustifications(payload: IProjectFeasibilityAndOptionsAnalysis & { project_id: number }, signal?: AbortSignal): Promise<ApiResponse<IProjectFeasibilityAndOptionsAnalysis>> {
    const response = await requestLaravel<ApiResponse<IProjectOptionRow[]>>(
        "/project/options/justifications/save",
        {
            project_id: payload.project_id,
            stage_id: STAGE_THREE_KEY,
            justification_of_option: payload.justification_of_option,
        },
        { signal }
    );

    if (response.status !== "ok" || !Array.isArray(response.data)) {
        return { ...response, data: payload };
    }

    return { ...response, data: { justification_of_option: toJustificationMap(response.data) } };
}

/* =========================
   RISK & QUALITY GATE
========================= */

// Only the Panoptic Evaluation Score; the risk rows save on the risk-management API.
export function getRiskAndQualityGate(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<Partial<IRiskAndQualityGate>>(project_id, RISK_AND_QUALITY_GATE_SECTION, signal);
}

export function updateRiskAndQualityGate(updateData: IRiskAndQualityGate & { project_id: number }) {
    return writeStageSection<IRiskAndQualityGate>(
        updateData.project_id,
        RISK_AND_QUALITY_GATE_SECTION,
        { panoptic_evaluation_score: updateData.panoptic_evaluation_score },
    );
}

/* =========================
   CBA / CEA INTELLIGENCE
========================= */

export function getCbaCeaIntelligence(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<Partial<ICbaCeaIntelligenceEngine>>(project_id, CBA_CEA_INTELLIGENCE_SECTION, signal);
}

export function updateCbaCeaIntelligence(updateData: ICbaCeaIntelligenceEngine & { project_id: number }) {
    return writeStageSection<ICbaCeaIntelligenceEngine>(updateData.project_id, CBA_CEA_INTELLIGENCE_SECTION, updateData);
}

/* =========================
   RISK & SENSITIVITY ANALYSIS
========================= */

export function getSensitivityAnalysis(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<Partial<IRiskAndSensitivityAnalysisEngine>>(project_id, SENSITIVITY_ANALYSIS_SECTION, signal);
}

export function updateSensitivityAnalysis(updateData: IRiskAndSensitivityAnalysisEngine & { project_id: number }) {
    return writeStageSection<IRiskAndSensitivityAnalysisEngine>(updateData.project_id, SENSITIVITY_ANALYSIS_SECTION, updateData);
}
